import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import NewReleasesRoundedIcon from '@material-ui/icons/NewReleasesRounded';
import AnnouncementRoundedIcon from '@material-ui/icons/AnnouncementRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import NotificationsRoundedIcon from '@material-ui/icons/NotificationsRounded';
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import FiberManualRecordRoundedIcon from '@material-ui/icons/FiberManualRecordRounded';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';

const cx = (...classNames) => classNames.filter(Boolean).join(' ');

const TYPE_ICONS = {
    release: NewReleasesRoundedIcon,
    announcement: AnnouncementRoundedIcon,
    system_alert: WarningRoundedIcon,
    other: NotificationsRoundedIcon
};

const TYPE_META = {
    release: { label: 'Release', color: '#6252c7', tint: '#f0edff' },
    announcement: { label: 'Announcement', color: '#16866f', tint: '#e6f7f1' },
    system_alert: { label: 'System Alert', color: '#dd6b20', tint: '#fff3e8' },
    other: { label: 'Update', color: '#4c5a72', tint: '#eef1f6' }
};

const STATUS_ICONS = {
    read: CheckCircleOutlineRoundedIcon,
    unread: FiberManualRecordRoundedIcon,
    alert: ErrorOutlineRoundedIcon
};

const DESCRIPTION_PREVIEW_LENGTH = 140;

const NotificationCard = ({ item, typeKey, status, description, formattedDate, onRead }) => {
    const [isExpanded, setExpanded] = useState(false);
    const meta = TYPE_META[typeKey];
    const TypeIcon = TYPE_ICONS[typeKey];
    const StatusIcon = STATUS_ICONS[status.key] || CheckCircleOutlineRoundedIcon;
    const isUnread = status.key !== 'read';
    const hasMoreContent = description.length > DESCRIPTION_PREVIEW_LENGTH;
    const visibleDescription = !isExpanded && hasMoreContent
        ? description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()
        : description;

    const handleToggleContent = () => {
        if (!isExpanded && isUnread && onRead) onRead(item);
        setExpanded(current => !current);
    };

    const handleMarkAsRead = () => {
        if (isUnread && onRead) onRead(item);
    };

    const handleCardKeyDown = (event) => {
        if (isUnread && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleMarkAsRead();
        }
    };

    return (
        <Box
            className={cx('notification-page-card', isUnread && 'is-unread can-mark-read')}
            style={{ '--type-color': meta.color, '--type-tint': meta.tint }}
            onClick={handleMarkAsRead}
            onKeyDown={handleCardKeyDown}
            role={isUnread ? 'button' : undefined}
            tabIndex={isUnread ? 0 : undefined}
            aria-label={isUnread ? `Mark ${item?.subject || 'notification'} as read` : undefined}
        >
            {isUnread && <Box className="notification-page-card-unread-dot" />}
            <Box className="notification-page-card-icon"> <TypeIcon /> </Box>
            <Box className="notification-page-card-body">
                <Box className="notification-page-card-top-row">
                    <Box className="notification-page-card-heading">
                        <Typography className="notification-page-card-type">{item?.notification_type?.label || meta.label}</Typography>
                        <Typography className="notification-page-card-subject">{item?.subject || '--'}</Typography>
                    </Box>
                    <Box className="notification-page-card-date-status">
                        <Typography className="notification-page-card-date">{formattedDate}</Typography>
                        <Box component="span" className={cx('notification-page-card-status', `is-${status.key}`)}>
                            <StatusIcon />
                            {status.label}
                        </Box>
                    </Box>
                </Box>
                {description && (
                    <Box className="notification-page-card-snippet">
                        <Box component="span">{visibleDescription}</Box>
                        {hasMoreContent && (
                            <Box component="button" type="button" className="notification-page-card-readmore" onClick={handleToggleContent} aria-expanded={isExpanded}>
                                {isExpanded ? 'Show less' : 'Read more'}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default NotificationCard;
